import Decimal from "decimal.js";
import { AlertTriangle } from "lucide-react";
import type { LeaveBalanceRow } from "../../api/types";

// 我的年度特休:額度、已用、剩餘(第七輪 Task 3)。
//
// 業主 2026-09-12 確認:「特休要算額度。每人一個年度特休天數(老闆設定),
// 扣掉已核准的,顯示剩餘。」
//
// --------------------------------------------------------------------------
// 三個數字一律由後端的 /api/leave/balance 說了算
// --------------------------------------------------------------------------
// 畫面上明明就有一份假單清單,看起來把它加一加就有「已用」了 —— 不可以。
// 那樣會把待核准的也算進去(還沒准的假不該先佔掉額度),也會把其他十二種
// 假別算進去(只有特休有額度可言),還會把去年的那幾張算進今年。
// 同一個數字有兩套算法,遲早對不起來,而那時扣掉的額度已經錯了。
//
// --------------------------------------------------------------------------
// 天數一律 decimal.js
// --------------------------------------------------------------------------
// 半天假讓天數出現 0.5,而 float 的 0.1 + 0.2 不是 0.3。這些數字會被一筆一筆
// 加起來扣年度特休,累積誤差最後會變成「你還有 0.0000001 天」這種沒人處理得了
// 的餘額。所以從後端到畫面全程是字串與 Decimal,中間不經過 number。

/**
 * 天數的顯示格式:`"14.0"` → `14`、`"0.5"` → `0.5`、`"-2.0"` → `-2`、`"0.125"` → `0.125`。
 *
 * 整數就不留 `.0` —— 「特休 14.0 天」比「特休 14 天」難讀。按小時請假
 * (業主 2026-11-03)之後 1 小時 = 0.125 天,所以最多留三位,多的尾零去掉。
 */
export function formatLeaveDays(value: string | number | null | undefined): string {
  const text = String(value ?? "").trim();
  const amount = toLeaveDecimal(text);
  // 看不懂的字串原樣回傳。寧可讓人看到怪字串,也不要整頁炸掉。
  if (amount === null) return text;
  const rounded = amount.toDecimalPlaces(3, Decimal.ROUND_HALF_UP);
  // Decimal 的 toString 不帶尾零:6.625 → "6.625"、0.5 → "0.5"、14 → "14"
  return rounded.isZero() ? "0" : rounded.toString();
}

/**
 * 天數字串 → Decimal。空字串當 0,認不得的回 `null`。
 *
 * `new Decimal("abc")` 會**丟例外**,而這些字串是後端回來的 —— 半成品資料、
 * 舊資料、或哪天欄位改了型別,都不該讓整頁白掉。
 */
function toLeaveDecimal(value: string): Decimal | null {
  try {
    return new Decimal(value.trim() === "" ? "0" : value.trim());
  } catch {
    return null;
  }
}

export interface LeaveBalanceProps {
  /** 登入者自己那一列。還沒撈到或老闆沒設過額度時是 null。 */
  row: LeaveBalanceRow | null;
  loading?: boolean;
}

/** 一格數字。名稱與數字綁在一起,不靠位置分辨誰是誰。 */
function Figure({ label, value, tone = "" }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="m-0 text-xs text-[var(--text-3)]">{label}</dt>
      <dd className={`tabular m-0 text-lg font-semibold ${tone === "" ? "text-[var(--text)]" : tone}`}>{value}</dd>
    </div>
  );
}

export function LeaveBalance({ row, loading = false }: LeaveBalanceProps) {
  const remaining = row === null ? null : toLeaveDecimal(row.remaining);
  const over = remaining !== null && remaining.isNegative();

  return (
    <section
      aria-label="我的特休"
      className="flex min-w-0 flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
    >
      <h3 className="m-0 text-sm font-semibold text-[var(--text)]">我的特休</h3>

      {loading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}

      {!loading && row === null ? (
        <p className="m-0 text-sm text-[var(--text-3)]">
          還沒有你的年度特休天數。年度天數由老闆在「設定 → 使用者」裡指定。
        </p>
      ) : null}

      {row ? (
        <dl className="m-0 flex flex-wrap items-start gap-x-8 gap-y-2">
          <Figure label="年度天數" value={`${formatLeaveDays(row.entitled)} 天`} />
          <Figure label="已用" value={`${formatLeaveDays(row.used)} 天`} />
          <Figure
            label="剩餘"
            value={`${formatLeaveDays(row.remaining)} 天`}
            tone={over ? "text-[var(--danger)]" : "text-[var(--accent-hover)]"}
          />
        </dl>
      ) : null}

      {/* 請超過額度不該靜靜地變成 0 —— 那會讓人以為還有得請。
          負數本身只是一個顏色不同的數字,所以再補一句話說清楚。 */}
      {over && remaining !== null ? (
        <p role="alert" className="m-0 flex items-center gap-1.5 text-sm text-[var(--danger)]">
          <AlertTriangle aria-hidden="true" size={16} className="shrink-0" />
          {`已經超出年度特休 ${formatLeaveDays(remaining.abs().toString())} 天,請先與老闆確認。`}
        </p>
      ) : null}
    </section>
  );
}
